import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { HouseholdPermissionService, HouseholdProfilesService, UserPersonaService } from '@se-po/shared-data-access-services'
import { EditPersonaModalComponent } from '@se-po/shared-feature-edit-persona-modal'
import { AddChildContactInfoComponent } from '@se-po/shared-ui-add-child-contact-info'
import { AddChildProfileComponent } from '@se-po/shared-ui-add-child-profile'
import {
  PersonaPickerComponent as UiPersonaPickerComponent
} from '@se-po/shared-ui-persona-picker'
import { SeFeModalButton, SeFeModalComponent, SeFeModalFooterComponent } from 'se-fe-modal'
import { SeFeThemesLoader } from 'se-fe-themes'
import {
  CommonResources as c,
  CentralServiceResources as cs,
  UserServiceResources as us
} from 'se-resource-types'

@Component({
  selector: 'se-po-persona-picker-element',
  templateUrl: './persona-picker.component.html',
  styleUrls: ['./persona-picker.component.scss'],
})
export class PersonaPickerComponent implements OnInit {
  @Input() public header?: string
  @Output() public personaPicked = new EventEmitter<cs.Households.UserProfile>()
  @ViewChild('addProfileModal') public addProfileModal: SeFeModalComponent
  @ViewChild('addProfileFooter') public addProfileFooter: SeFeModalFooterComponent
  @ViewChild('childInfo') public childInfo: AddChildProfileComponent
  @ViewChild('childContactInfo') public childContactInfo: AddChildContactInfoComponent
  @ViewChild('editPersonaModal') public editPersonaModal: EditPersonaModalComponent
  @ViewChild('picker') public picker: UiPersonaPickerComponent

  public ready = false
  public profiles: cs.Households.UserProfile[]
  public selfProfile: cs.Households.UserProfile
  public selfPersona: cs.Profiles.Persona
  public permissions: HouseholdPermissionService
  public managedProfiles: cs.Households.UserProfile[]
  public editInfoProfile: cs.Households.UserProfile
  public themeLoaded = false
  public userId: number
  public addChildStep = 1
  public addProfileStep1Buttons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => this.cancelAddPersona()
    },
    {
      primary: true,
      label: 'Add Profile',
      action: async () => await this.addProfile(),
      loadingLabel: 'Saving Profile',
    }
  ]
  public addProfileStep2Buttons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => this.cancelAddPersona()
    },
    {
      primary: true,
      label: 'Continue',
      action: async () => await this.addContactInfo(),
      loadingLabel: 'Saving',
    }
  ]
  public addProfileButtons = this.addProfileStep1Buttons
  public addPersona: us.Persona = {} as us.Persona

  constructor(
    private el: ElementRef,
    private userPersonaService: UserPersonaService,
    private householdProfilesService: HouseholdProfilesService,
    private translateService: TranslateService
  ) {
    this.loadTheme()
  }

  public ngOnInit(): void {
    this.translateService.setDefaultLang('en')
    this.loadProfiles()
  }

  public onEdit(p: cs.Households.UserProfile): void {
    this.editInfoProfile = p
    this.editPersonaModal.open(this.editInfoProfile)
  }

  public onPick(p: cs.Households.UserProfile): void {
    this.personaPicked.emit(p)
  }

  public onAddProfileClick(evt): void {
    this.addChildStep = 1
    this.addProfileButtons = this.addProfileStep1Buttons
    this.childInfo.reset()
    this.childContactInfo.reset()
    this.addProfileModal.open(evt)
  }

  public onPersonaSave(): void {
    this.ready = false
    this.loadProfiles()
  }

  private loadProfiles(): void {
    this.householdProfilesService.mine()
      .subscribe(
        (result: cs.Households.UserProfilesResult) => {
          if (!result) return

          this.permissions = HouseholdPermissionService.forAccount(result)
          this.profiles = this.permissions.profiles
          this.selfProfile = this.permissions.selfProfile
          this.selfPersona = this.selfProfile.persona
          this.managedProfiles = this.permissions.managedProfiles
          this.userId = this.permissions.user.id
          this.ready = true
        }
      )
  }

  private async addProfile(): Promise<void> {
    if (!this.childInfo.isValid()) {
      this.addProfileFooter.cancelSpinner()
      return
    }

    const addPersona = {
      first_name: this.childInfo.value.name?.first,
      last_name: this.childInfo.value.name?.last,
      suffix: this.childInfo.value.name?.suffix,
      date_of_birth: this.childInfo.value.dateOfBirth,
      gender: this.childInfo.value.gender as c.Gender,
    } as us.Persona
    this.childContactInfo.persona = addPersona
    this.childContactInfo.description = 'Only add a phone or email' +
      ' if you’d like ' + addPersona.first_name +
      ' to receive their own messages.'
    this.addChildStep = 2
    this.addProfileButtons = this.addProfileStep2Buttons
  }

  private async addContactInfo(): Promise<void> {
    if (!this.childContactInfo.isValid()) {
      this.addProfileFooter.cancelSpinner()
      return
    }

    const data = {
      ...this.childInfo.value,
      ...this.childContactInfo.value,
    }
    const persona = this.userPersonaService
      .create(this.userId, data)
      .subscribe(result => {
        const newPersonaId = result.id
        this.householdProfilesService.findMyProfiles()
          .subscribe(profiles => {
            this.profiles = profiles
            this.addProfileFooter.cancelSpinner()
            this.addProfileModal.close()
            this.onPick(this.profiles.find(p => p.persona.id === newPersonaId))
          })
      })
  }

  private cancelAddPersona(): void {
    this.addProfileFooter.cancelSpinner()
    this.addProfileModal.close()
  }

  private async loadTheme(): Promise<void> {
    await SeFeThemesLoader.load({
      supportedThemes: 'light|dark',
      shadowRoot: this.el.nativeElement.shadowRoot,
      url: 'https://se-fe-angular-services.ui.sportngin.com',
    })
    this.themeLoaded = true
  }
}
